'use client';

import { type MouseEvent, useEffect, useRef } from 'react';

const ANIMATION_DURATION = 100;

type UseAccordionProps = {
    defaultOpen?: boolean;
    onOpen?: () => void;
    onClose?: () => void;
};

export const useAccordion = ({ defaultOpen, onOpen, onClose }: UseAccordionProps = {}) => {
    const detailsRef = useRef<HTMLDetailsElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<number | null>(null);

    const handleClick = (e: MouseEvent<HTMLElement>) => {
        e.preventDefault();
        const details = detailsRef.current;
        const content = contentRef.current;

        if (!details || !content) return;

        if (timeoutRef.current !== null) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        if (details.open) {
            details.dataset.state = 'closed';
            const height = content.scrollHeight;
            content.style.height = `${height}px`;

            requestAnimationFrame(() => (content.style.height = '0px'));
            timeoutRef.current = setTimeout(() => {
                details.open = false;
                timeoutRef.current = null;
                onClose?.();
            }, ANIMATION_DURATION) as unknown as number;
        } else {
            details.dataset.state = 'opened';
            details.open = true;
            const height = content.scrollHeight;
            content.style.height = '0px';

            requestAnimationFrame(() => (content.style.height = `${height}px`));

            // Needs for nested dynamic hight content (async content, images with lazy loading, another disclosure, etc.)
            timeoutRef.current = setTimeout(() => {
                content.style.height = 'auto';
                timeoutRef.current = null;
                onOpen?.();
            }, ANIMATION_DURATION) as unknown as number;
        }
    };

    useEffect(() => {
        const details = detailsRef.current;
        const content = contentRef.current;

        if (details && content && defaultOpen) {
            details.open = true;
            content.style.height = 'auto';
            details.dataset.state = 'opened';
        }
    }, []);

    useEffect(() => {
        return () => {
            timeoutRef.current !== null && clearTimeout(timeoutRef.current);
        };
    }, []);

    return { detailsRef, contentRef, handleClick };
};
